import * as React from "react";

import { Checkbox } from "@/components/ui/Checkbox";

import { ChevronDown } from "lucide-react";

import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandRoot,
} from "@/components/ui/Command";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";

import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/utils";

interface ComboboxProps {
  selectedOptions: ComboboxOption[];
  onChange: React.Dispatch<React.SetStateAction<ComboboxOption[]>>;
  options: ComboboxOption[];
  itemText: string;
  className?: string;
}

export interface ComboboxOption {
  value: string;
  label: string;
}

export function Combobox({
  options,
  selectedOptions,
  itemText,
  onChange,
  className = "",
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  const handleChange = (option: ComboboxOption) => {
    const checked = !selectedOptions.some(
      ({ value }) => value === option.value,
    );

    if (checked) {
      onChange((prev) => [...prev, option]);
    } else
      onChange((prev) => prev.filter(({ value }) => value !== option.value));
  };

  const renderTriggerText = () => {
    const counterText = `(${selectedOptions.length - 1}+ more)`;

    if (!selectedOptions.length) return `All ${itemText}`;
    return `${selectedOptions[0].label} ${selectedOptions.length > 1 ? counterText : ""}`;
  };

  React.useEffect(() => {
    if (!open) setTouched(false);
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <CommandRoot className="w-full">
          <button
            type="button"
            className={cn(
              "w-full border bg-white border-secondary rounded-lg px-2 py-1 focus:border-link flex justify-between items-center h-8 text-xs active:border-link",
              open ? "border-link" : "",
              className,
            )}
          >
            <span className="truncate">{renderTriggerText()}</span>
            <ChevronDown className="w-4" />
          </button>
        </CommandRoot>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[200px] border-primary border rounded-md p-0 pt-3"
      >
        <CommandRoot>
          <div className="px-3">
            <Label className="mb-1">Search {itemText}</Label>
            <CommandInput
              className="h-7"
              style={{ height: "22px" }}
              placeholder=""
              onChangeCapture={(e) =>
                setTouched(!!(e.target as HTMLInputElement).value)
              }
            />
          </div>
          <div className="mt-2">
            {!touched && (
              <div className="hover:bg-secondary/50 flex flex-1 items-center px-3 cursor-pointer">
                <Checkbox
                  checked={!selectedOptions.length}
                  onClick={() => onChange([])}
                  id="combo-box-default"
                />
                <Label
                  htmlFor="combo-box-default"
                  className="font-normal pl-2 py-1.5 cursor-pointer"
                >
                  All {itemText}
                </Label>
              </div>
            )}

            <CommandList className="relative top-0">
              <CommandEmpty
                noResultsText="No results found."
                className="px-3 mb-2 border-none"
              />

              <CommandGroup className="border-none" style={{ padding: "0px" }}>
                {options.map((option) => (
                  <CommandItem
                    className="data-[selected=true]:bg-white data-[selected=true]:hover:bg-secondary/50 flex flex-row flex-1 items-center py-2 px-3"
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleChange(option)}
                  >
                    <Checkbox
                      checked={selectedOptions.some(
                        ({ value }) => value === option.value,
                      )}
                      onClick={(e) => e.preventDefault()}
                      id={`combo-box-${option.value}`}
                    />
                    <Label
                      htmlFor={`combo-box-${option.value}`}
                      className="font-normal pl-2 cursor-pointer"
                      onClick={(e: React.MouseEvent) => e.preventDefault()}
                    >
                      {option.label}
                    </Label>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </div>
        </CommandRoot>
      </PopoverContent>
    </Popover>
  );
}
